import { useMemo, useState } from "react";
import useLocalStorage from "use-local-storage";
import { IconPlus, IconUserSquareRounded } from "@tabler/icons-react";
import ProfileListCard from "./ProfileListCard";
import Button from "../UI/Button";
import EmptyState from "../UI/EmptyState";
import Text from "../UI/Text";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
// @ts-expect-error pure js
import { StatusFiscal } from "../../models/profile";
import FilterBar from "../UI/FilterBar/FilterBar.tsx";
import FilterSection from "../UI/FilterBar/FilterSection.tsx";
import type { ProfileBailleur } from "./types";
import styles from "./ProfileList.module.css";

const TYPE_OPTIONS = [
  { value: "particulier", label: "Particulier" },
  { value: "societe", label: "Société" },
];

const REGIME_FISCAL_OPTIONS = [
  { value: StatusFiscal.NUMICRO, label: "Micro foncier" },
  { value: StatusFiscal.NUREEL, label: "Réel foncier" },
  { value: StatusFiscal.LMNPMICRO, label: "LMNP micro-BIC" },
  { value: StatusFiscal.LMNPREEL, label: "LMNP réel" },
  { value: StatusFiscal.IS, label: "Société à l'IS" },
];

interface ProfileListProps {
  onAddProfile: () => void;
}

export default function ProfileList({ onAddProfile }: ProfileListProps) {
  const { profileBailleurs, isLoading } = useProfileBailleurs() as {
    profileBailleurs: ProfileBailleur[];
    isLoading: boolean;
  };
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useLocalStorage<string[]>(
    "bailleurList_typeFilter",
    [],
  );
  const [regimeFilter, setRegimeFilter] = useLocalStorage<string[]>(
    "bailleurList_regimeFilter",
    [],
  );

  const filtered = useMemo(() => {
    const searchLower = search.toLowerCase();
    return profileBailleurs.filter((profile) => {
      if (
        searchLower &&
        !profile.nom_profile?.toLowerCase().includes(searchLower)
      ) {
        return false;
      }
      if (typeFilter && typeFilter.length > 0) {
        const isSociete = !!profile.is_societe;
        const wantsSociete = typeFilter.includes("societe");
        const wantsParticulier = typeFilter.includes("particulier");
        if (wantsSociete && !wantsParticulier && !isSociete) return false;
        if (wantsParticulier && !wantsSociete && isSociete) return false;
      }
      if (regimeFilter && regimeFilter.length > 0) {
        if (
          !profile.regime_fiscal ||
          !regimeFilter.includes(profile.regime_fiscal)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [profileBailleurs, search, typeFilter, regimeFilter]);

  const activeFiltersCount =
    (typeFilter.length > 0 ? 1 : 0) + (regimeFilter.length > 0 ? 1 : 0);

  function clearFilters() {
    setTypeFilter([]);
    setRegimeFilter([]);
  }

  if (isLoading) return <IsLoading />;

  if (profileBailleurs.length === 0) {
    return (
      <EmptyState
        icon={IconUserSquareRounded}
        title="Aucun profil bailleur pour le moment"
        description="Le profil bailleur représente le propriétaire qui met en location, en nom propre ou en société. Créez-en un pour ajouter vos biens et commencer votre gestion."
      >
        <Button icon={IconPlus} onClick={onAddProfile}>
          Ajouter un profil bailleur
        </Button>
      </EmptyState>
    );
  }

  return (
    <>
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Rechercher un bailleur"
        activeFiltersCount={activeFiltersCount}
        onClearFilters={clearFilters}
        addLabel="Ajouter un profil bailleur"
        onAdd={onAddProfile}
      >
        <FilterSection
          title="Type"
          options={TYPE_OPTIONS}
          values={typeFilter}
          onChange={setTypeFilter}
          defaultOpen
        />
        <FilterSection
          title="Régime fiscal"
          options={REGIME_FISCAL_OPTIONS}
          values={regimeFilter}
          onChange={setRegimeFilter}
        />
      </FilterBar>

      {filtered.length > 0 ? (
        <div className={styles.grid}>
          {filtered.map((profileBailleur) => (
            <ProfileListCard
              key={profileBailleur.id}
              profileBailleur={profileBailleur}
            />
          ))}
        </div>
      ) : (
        <Text as="div" color="muted">
          Aucun bailleur trouvé
        </Text>
      )}
    </>
  );
}
